import { createContext, useContext, useEffect, useMemo, useState, useCallback, ReactNode } from "react";
import { supabase, AuthUser, Session } from "@/integrations/supabase/client";

type AuthError = string | null;

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  /** Non-null when auth/profile bootstrap failed. */
  error: AuthError;
  /** True only after we confirmed a profile row exists for the authenticated user. */
  profileReady: boolean;
  role: string;
  roleLabel: string;
  signIn: (email: string, password: string) => Promise<AuthUser | null>;
  signUp: (email: string, password: string, metadata?: any) => Promise<AuthUser | null>;
  signOut: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function getRoleLabel(role: string) {
  const labels: Record<string, string> = {
    super_admin: "Super Admin",
    company_admin: "Company Admin",
    ca: "Chartered Accountant",
    accountant: "Accountant",
  };
  return labels[role] || "User";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError>(null);
  const [profileReady, setProfileReady] = useState(false);

  const log = (...args: any[]) => console.debug("[MySQL Auth]", ...args);

  const ensureProfile = useCallback(async (sessionUser: AuthUser) => {
    log("user fetch", { userId: sessionUser.id, email: sessionUser.email });

    // 1) Try read profile
    const profileRes = await supabase
      .from("profiles")
      .select("id,email,role,name,company_id,created_at")
      .eq("id", sessionUser.id)
      .maybeSingle();

    if (profileRes.error) {
      throw profileRes.error;
    }

    if (profileRes.data) return profileRes.data;

    // 2) Create profile if missing
    const email = sessionUser.email;
    if (!email) {
      throw new Error("Authenticated user has no email; cannot create profile.");
    }

    const roleFromMeta = (sessionUser.user_metadata?.role as string | undefined) ?? sessionUser.role ?? "company_admin";

    log("user missing profile → creating", { role: roleFromMeta });

    const insertRes = await supabase
      .from("profiles")
      .insert({
        id: sessionUser.id,
        email,
        role: roleFromMeta,
        name: sessionUser.name || email.split("@")[0],
      })
      .select("id,email,role,name,company_id,created_at")
      .single();

    if (insertRes.error) throw insertRes.error;
    return insertRes.data;
  }, []);

  const applySession = useCallback(async (session: Session | null, source: string): Promise<AuthUser | null> => {
    try {
      setLoading(true);
      setError(null);

      log("session", source, {
        hasSession: !!session,
        userId: session?.user?.id,
      });

      if (!session?.user) {
        setUser(null);
        setProfileReady(false);
        return null;
      }

      const profile = await ensureProfile(session.user);

      log("company fetch", { company_id: profile?.company_id ?? null });

      const authUser: AuthUser = {
        ...session.user,
        name: profile?.name || (session.user.user_metadata?.name as string | undefined) || session.user.name,
        role: profile?.role || session.user.role || "company_admin",
        company: profile?.company_id ?? session.user.company ?? null,
        company_id: profile?.company_id ?? session.user.company_id ?? null,
      };

      setUser(authUser);
      setProfileReady(true);
      return authUser;
    } catch (e: any) {
      log("bootstrap error", e);
      setUser(null);
      setProfileReady(false);
      setError("Unable to load profile from database. Please refresh.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [ensureProfile]);

  useEffect(() => {
    let cancelled = false;

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (!cancelled) {
        void applySession(session, `onAuthStateChange:${event}`);
      }
    });

    const loadLocalSession = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (cancelled) return;
        if (user) {
          const fakeSession: Session = { access_token: "token-local", user };
          await applySession(fakeSession, "localInit");
        } else {
          await applySession(null, "localInit");
        }
      } catch (e) {
        log("local init error", e);
        if (!cancelled) {
          setError("Unable to load dashboard data. Please refresh.");
          setLoading(false);
        }
      }
    };

    loadLocalSession();

    return () => {
      cancelled = true;
      data.subscription.unsubscribe();
    };
  }, [applySession]);

  const signIn = async (email: string, password: string): Promise<AuthUser | null> => {
    setError(null);
    setLoading(true);

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      throw error;
    }

    if (data?.session) {
      return await applySession(data.session, "signIn");
    }
    setLoading(false);
    return null;
  };

  const signUp = async (email: string, password: string, metadata: any = {}): Promise<AuthUser | null> => {
    setError(null);
    setLoading(true);

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });

    if (error) {
      setLoading(false);
      throw error;
    }

    if (data?.session) {
      return await applySession(data.session, "signUp");
    }
    setLoading(false);
    return null;
  };

  const refreshUser = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const fakeSession: Session = { access_token: "token-local", user: currentUser };
        await applySession(fakeSession, "refreshUser");
      }
    } catch (e) {
      log("refreshUser error", e);
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {}
    setUser(null);
    setProfileReady(false);

    try {
      localStorage.removeItem("accoupro_auth_user");
      localStorage.removeItem("accoupro_auth_session");
      localStorage.removeItem("accoupro_active_company_id");
    } catch {}

    window.location.assign("/login");
  };

  const value = useMemo<AuthContextType>(() => {
    const role = user?.role || "user";
    return {
      user,
      loading,
      error,
      profileReady,
      role,
      roleLabel: getRoleLabel(role),
      signIn,
      signUp,
      signOut,
      logout: signOut,
      isAuthenticated: !!user,
      refreshUser,
    };
  }, [user, loading, error, profileReady]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
